<template>
	<main class="my-profile-page">
		<header class="topbar">
			<RouterLink to="/" class="back-link" :aria-label="$t('my-profile.back-to-map')">
				<AppIcon :name="IconName.ArrowLeft" :size="18" :stroke-width="2" />
				<span>{{ $t("my-profile.back-to-map") }}</span>
			</RouterLink>
		</header>

		<Transition name="profile-fade" mode="out-in">
			<section
				v-if="isInitialProfileLoading && store.isUserLoggedIn"
				key="loading"
				class="profile-shell profile-skeleton"
				aria-live="polite"
				aria-busy="true"
				:aria-label="$t('my-profile.status.loading')"
			>
				<header class="profile-header">
					<span class="skeleton-block skeleton-avatar" aria-hidden="true"></span>
					<div class="identity skeleton-identity">
						<span class="skeleton-block skeleton-eyebrow" aria-hidden="true"></span>
						<span class="skeleton-block skeleton-title" aria-hidden="true"></span>
						<span class="skeleton-block skeleton-line medium" aria-hidden="true"></span>
					</div>
					<div class="role-list skeleton-roles" aria-hidden="true">
						<span class="skeleton-block skeleton-chip"></span>
						<span class="skeleton-block skeleton-chip short"></span>
					</div>
				</header>

				<section class="phone-section" aria-hidden="true">
					<div class="section-heading">
						<div class="skeleton-heading">
							<span class="skeleton-block skeleton-eyebrow"></span>
							<span class="skeleton-block skeleton-section-title"></span>
						</div>
						<span class="skeleton-block skeleton-status"></span>
					</div>

					<div class="phone-panel skeleton-panel">
						<div class="skeleton-field">
							<span class="skeleton-block skeleton-label"></span>
							<span class="skeleton-block skeleton-input"></span>
						</div>
						<span class="skeleton-block skeleton-button"></span>
					</div>
				</section>

				<section class="preferences-section" aria-hidden="true">
					<div class="section-heading">
						<div class="skeleton-heading">
							<span class="skeleton-block skeleton-eyebrow"></span>
							<span class="skeleton-block skeleton-section-title wide"></span>
						</div>
						<span class="skeleton-block skeleton-status"></span>
					</div>

					<article class="language-card skeleton-language-card">
						<div class="card-main">
							<span class="skeleton-block skeleton-icon"></span>
							<div class="card-copy skeleton-copy">
								<span class="skeleton-block skeleton-card-title"></span>
								<span class="skeleton-block skeleton-line"></span>
							</div>
						</div>
						<div class="language-options skeleton-language-options">
							<span class="skeleton-block skeleton-language-option"></span>
							<span class="skeleton-block skeleton-language-option"></span>
						</div>
					</article>

					<div class="notification-heading">
						<span class="skeleton-block skeleton-eyebrow"></span>
						<span class="skeleton-block skeleton-section-title"></span>
					</div>

					<div class="preference-grid">
						<article v-for="index in 3" :key="index" class="preference-card skeleton-preference-card">
							<div class="card-main">
								<span class="skeleton-block skeleton-icon"></span>
								<div class="card-copy skeleton-copy">
									<div class="title-row">
										<span class="skeleton-block skeleton-card-title"></span>
										<span class="skeleton-block skeleton-toggle"></span>
									</div>
									<span class="skeleton-block skeleton-line"></span>
									<span class="skeleton-block skeleton-line short"></span>
								</div>
							</div>

							<div class="frequency-row">
								<span class="skeleton-block skeleton-label"></span>
								<span class="skeleton-block skeleton-frequency"></span>
							</div>
						</article>
					</div>
				</section>
			</section>

			<section v-else-if="store.isUserLoggedIn" key="content" class="profile-shell">
				<header class="profile-header">
					<div class="avatar" aria-hidden="true">{{ initials }}</div>
					<div class="identity">
						<p class="eyebrow">{{ $t("my-profile.title") }}</p>
						<h1>{{ fullName }}</h1>
						<p class="email">{{ email }}</p>
					</div>
					<div class="role-list" v-if="roles.length > 0" aria-label="Roles">
						<span v-for="role in roles" :key="role" class="role-chip">{{ role }}</span>
					</div>
				</header>

				<section class="phone-section" aria-labelledby="phone-number-title">
					<div class="section-heading">
						<div>
							<p class="eyebrow">{{ $t("my-profile.phone.eyebrow") }}</p>
							<h2 id="phone-number-title">{{ $t("my-profile.phone.title") }}</h2>
						</div>
						<span
							class="status-pill"
							:class="{ verified: isPhoneVerified, warning: hasSavedPhoneNumber && !isPhoneVerified }"
						>
							{{ phoneStatusLabel }}
						</span>
					</div>

					<form class="phone-form-panel" @submit.prevent>
						<label v-if="phoneStep === PhoneStep.Edit">
							<span>{{ $t("my-profile.phone.label") }}</span>
							<input
								v-model="phoneNumberDraft"
								type="tel"
								autocomplete="tel"
								:placeholder="$t('my-profile.phone.placeholder')"
								@keyup.enter="savePhoneNumberFlow"
							/>
						</label>

						<div v-else-if="phoneStep === PhoneStep.Confirm" class="confirm">
							<label>
								<span>{{ $t("my-profile.phone.code-label") }}</span>
								<input
									v-model="phoneVerificationCode"
									type="text"
									inputmode="numeric"
									autocomplete="one-time-code"
									:placeholder="$t('my-profile.phone.code-placeholder')"
									@keyup.enter="confirmPhoneVerificationFlow"
								/>
							</label>
						</div>

						<div v-else class="saved-number">
							<span>{{ $t("my-profile.phone.label") }}</span>
							<strong>{{ formattedSavedPhoneNumber }}</strong>
							<p>{{ phoneNote }}</p>
						</div>

						<p v-if="phoneVerificationError" class="feedback error">{{ phoneVerificationError }}</p>
						<p v-else-if="phoneVerificationMessage" class="feedback success">{{ phoneVerificationMessage }}</p>

						<div class="buttons">
							<template v-if="phoneStep === PhoneStep.Edit">
								<Button variant="tertiary" size="s" @click="cancelPhoneNumberChange">
									{{ $t("my-profile.phone.actions.cancel") }}
								</Button>
								<Button variant="primary" size="s" :disabled="!canSavePhoneNumber" @click="savePhoneNumberFlow">
									{{ savePhoneNumberButtonLabel }}
								</Button>
							</template>

							<template v-else-if="phoneStep === PhoneStep.Verify">
								<Button variant="tertiary" size="s" @click="editPhoneNumber">
									{{ $t("my-profile.phone.actions.change") }}
								</Button>
								<Button
									variant="primary"
									size="s"
									:disabled="!canStartPhoneVerification"
									@click="startPhoneVerificationFlow"
								>
									{{ startPhoneVerificationButtonLabel }}
								</Button>
							</template>

							<template v-else-if="phoneStep === PhoneStep.Confirm">
								<Button
									variant="tertiary"
									size="s"
									:disabled="!canStartPhoneVerification"
									@click="startPhoneVerificationFlow"
								>
									{{ startPhoneVerificationButtonLabel }}
								</Button>
								<Button
									variant="primary"
									size="s"
									:disabled="!canConfirmPhoneVerification"
									@click="confirmPhoneVerificationFlow"
								>
									{{ confirmPhoneVerificationButtonLabel }}
								</Button>
							</template>

							<Button v-else variant="tertiary" size="s" @click="editPhoneNumber">
								{{ $t("my-profile.phone.actions.change") }}
							</Button>
						</div>
					</form>
				</section>

				<section class="preferences-section" aria-labelledby="profile-preferences-title">
					<div class="section-heading">
						<div>
							<p class="eyebrow">{{ $t("my-profile.preferences.eyebrow") }}</p>
							<h2 id="profile-preferences-title">{{ $t("my-profile.preferences.title") }}</h2>
						</div>
						<span class="status-pill" :class="{ dirty: hasPendingChanges, error: preferencesError }">{{
							statusLabel
						}}</span>
					</div>

					<p v-if="preferencesError" class="error-message">{{ preferencesError }}</p>

					<article class="language-card">
						<div class="card-main">
							<span class="channel-icon language" aria-hidden="true">
								<AppIcon :name="IconName.Run" :size="22" :stroke-width="1.8" />
							</span>

							<div class="card-copy">
								<h3>{{ $t("my-profile.language.title") }}</h3>
								<p>{{ $t("my-profile.language.description") }}</p>
							</div>
						</div>

						<div class="language-options" role="radiogroup" :aria-label="$t('my-profile.language.title')">
							<label
								v-for="option in languageOptions"
								:key="option.id"
								class="language-option"
								:class="{ selected: languageDraft === option.id }"
							>
								<input
									v-model="languageDraft"
									type="radio"
									name="preferred-language"
									:value="option.id"
									:disabled="isLoadingPreferences || isSavingPreferences"
								/>
								<span>{{ option.label }}</span>
							</label>
						</div>
					</article>

					<div class="notification-heading">
						<p class="eyebrow">{{ $t("my-profile.notifications.eyebrow") }}</p>
						<h3>{{ $t("my-profile.notifications.title") }}</h3>
					</div>

					<div class="preference-grid">
						<article v-for="preference in preferences" :key="preference.channel" class="preference-card">
							<div class="card-main">
								<span class="channel-icon" :class="preference.channel" aria-hidden="true">
									<AppIcon
										v-if="preference.channel === 'in_app'"
										:name="IconName.Bell"
										:size="22"
										:stroke-width="1.8"
									/>
									<AppIcon
										v-else-if="preference.channel === 'email'"
										:name="IconName.Mail"
										:size="22"
										:stroke-width="1.8"
									/>
									<AppIcon v-else :name="IconName.Smartphone" :size="22" :stroke-width="1.8" />
								</span>

								<div class="card-copy">
									<div class="title-row">
										<h3>{{ getPreferenceLabel(preference.channel) }}</h3>
										<label
											class="channel-toggle"
											:class="{ enabled: isPreferenceChecked(preference), disabled: isPreferenceDisabled(preference) }"
										>
											<input
												type="checkbox"
												:checked="isPreferenceChecked(preference)"
												:disabled="isPreferenceDisabled(preference)"
												:aria-label="getPreferenceAriaLabel(preference.channel)"
												@change="setPreferenceEnabled(preference, ($event.target as HTMLInputElement).checked)"
											/>
											<span>
												{{
													isPreferenceChecked(preference)
														? $t("my-profile.notifications.enabled")
														: $t("my-profile.notifications.disabled")
												}}
											</span>
										</label>
									</div>
									<p>{{ getPreferenceDescription(preference.channel) }}</p>
									<p v-if="preference.channel === 'sms' && isSmsPreferenceBlocked" class="warning-message">
										{{ $t("my-profile.notifications.sms-requires-verified-phone") }}
									</p>
								</div>
							</div>

							<div class="frequency-row">
								<span class="frequency-label">{{ $t("my-profile.notifications.frequency") }}</span>
								<div class="immediate-frequency" :class="{ disabled: !isPreferenceChecked(preference) }">
									<span>{{ getFrequencyLabel(immediateFrequency) }}</span>
								</div>
							</div>
						</article>
					</div>

					<footer class="actions">
						<Button
							variant="tertiary"
							size="m"
							:disabled="!hasPendingChanges || isLoadingPreferences || isSavingPreferences"
							@click="resetPreferences"
						>
							{{ $t("my-profile.actions.reset") }}
						</Button>
						<Button
							variant="primary"
							size="m"
							:disabled="!hasPendingChanges || isLoadingPreferences || isSavingPreferences"
							@click="savePreferences"
						>
							{{ saveButtonLabel }}
						</Button>
					</footer>
				</section>
			</section>
		</Transition>
	</main>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"

import Button from "@/components/ui/Button.vue"
import { RedirectReason, UseRootStore } from "@/model/RootStore"
import { PhoneStep, usePhoneVerification } from "@/composables/usePhoneVerification"
import {
	immediateFrequency,
	useNotificationPreferences,
	type NotificationChannel,
	type NotificationFrequency,
	type PreferenceViewModel,
} from "@/composables/useNotificationPreferences"
import { useLanguagePreference } from "@/composables/useLanguagePreference"
import { getUserInitials } from "@/utils/user"

const store = UseRootStore()
const router = useRouter()
const { t } = useI18n()

const wasSaved = ref(false)
const isSavingPreferences = ref(false)
const isInitialProfileLoading = ref(true)

const user = computed(() => store.currentUser)
const userInfo = computed(() => user.value.getInfo())
const fullName = computed(() => user.value.getFullName())
const email = computed(() => userInfo.value?.email ?? "")
const roles = computed(() => user.value.getRoles())

const initials = computed(() => getUserInitials(fullName.value, "LA"))

const {
	phoneStep,
	phoneNumberDraft,
	phoneVerificationCode,
	phoneVerificationMessage,
	phoneVerificationError,
	hasSavedPhoneNumber,
	formattedSavedPhoneNumber,
	isPhoneVerified,
	isSmsPreferenceBlocked,
	canSavePhoneNumber,
	canStartPhoneVerification,
	canConfirmPhoneVerification,
	phoneStatusLabel,
	phoneNote,
	savePhoneNumberButtonLabel,
	startPhoneVerificationButtonLabel,
	confirmPhoneVerificationButtonLabel,
	editPhoneNumber,
	cancelPhoneNumberChange,
	savePhoneNumberFlow,
	startPhoneVerificationFlow,
	confirmPhoneVerificationFlow,
} = usePhoneVerification()

const {
	preferences,
	isLoadingPreferences,
	preferencesError,
	hasNotificationPendingChanges,
	isPreferenceDisabled,
	isPreferenceChecked,
	loadPreferences,
	setPreferenceEnabled: setPreferenceEnabledBase,
	resetPreferences: resetNotificationPreferences,
	saveNotificationPreferences,
} = useNotificationPreferences(isSmsPreferenceBlocked, isSavingPreferences)

const {
	languageDraft,
	languageOptions,
	hasLanguagePendingChanges,
	syncLanguageFromProfile,
	saveLanguagePreference,
	resetLanguagePreference,
} = useLanguagePreference()

const hasPendingChanges = computed(() => hasNotificationPendingChanges.value || hasLanguagePendingChanges.value)

const statusLabel = computed(() => {
	if (preferencesError.value) return t("my-profile.status.error")
	if (isLoadingPreferences.value) return t("my-profile.status.loading")
	if (isSavingPreferences.value) return t("my-profile.status.saving")
	if (hasPendingChanges.value) return t("my-profile.status.unsaved")
	if (wasSaved.value) return t("my-profile.status.saved")
	return t("my-profile.status.current")
})

const saveButtonLabel = computed(() =>
	isSavingPreferences.value
		? t("my-profile.status.saving")
		: wasSaved.value && !hasPendingChanges.value
			? t("my-profile.status.saved")
			: t("my-profile.actions.save")
)

const getPreferenceLabel = (channel: NotificationChannel) => t(`my-profile.notifications.channels.${channel}.label`)
const getPreferenceDescription = (channel: NotificationChannel) =>
	t(`my-profile.notifications.channels.${channel}.description`)
const getPreferenceAriaLabel = (channel: NotificationChannel) =>
	t("my-profile.notifications.channel-aria-label", { channel: getPreferenceLabel(channel) })
const getFrequencyLabel = (frequency: NotificationFrequency) => t(`my-profile.notifications.frequencies.${frequency}`)

const setPreferenceEnabled = (preference: PreferenceViewModel, enabled: boolean) => {
	setPreferenceEnabledBase(preference, enabled)
	wasSaved.value = false
}

const resetPreferences = () => {
	resetNotificationPreferences()
	resetLanguagePreference()
	wasSaved.value = false
	preferencesError.value = null
}

const savePreferences = async () => {
	if (!store.isUserLoggedIn || isSavingPreferences.value) return

	isSavingPreferences.value = true
	preferencesError.value = null
	try {
		await saveNotificationPreferences()
		await saveLanguagePreference()
		wasSaved.value = true
	} catch (error) {
		preferencesError.value = error instanceof Error ? error.message : String(error)
	} finally {
		isSavingPreferences.value = false
	}
}

onMounted(async () => {
	if (!store.isUserLoggedIn) {
		await router.replace({ path: "/login", query: { reason: RedirectReason.LoginRequired } })
		return
	}

	try {
		await Promise.all([store.fetchBasicCurrentUserDetails(), loadPreferences()])
		syncLanguageFromProfile()
	} finally {
		isInitialProfileLoading.value = false
	}
})
</script>

<style scoped>
.my-profile-page {
	height: 100dvh;
	overflow-y: auto;
	overflow-x: hidden;
	background: var(--neutral-50);
	color: var(--neutral-900);
	font-family: var(--font-family-primary);
}

.topbar {
	display: flex;
	align-items: center;
	height: var(--space-64);
	padding: 0 var(--spacing-xl);
	border-bottom: var(--border-width-thin) solid var(--neutral-200);
	background: var(--neutral-0);
}

.back-link {
	display: inline-flex;
	align-items: center;
	gap: var(--spacing-sm);
	color: var(--neutral-600);
	text-decoration: none;
	font-size: var(--font-size-base);
	font-weight: var(--font-weight-medium);

	&:hover {
		color: var(--primary-green);
	}
}

.profile-fade-enter-active,
.profile-fade-leave-active {
	transition:
		opacity 0.18s ease,
		transform 0.18s ease;
}

.profile-fade-enter-from,
.profile-fade-leave-to {
	opacity: 0;
	transform: translateY(var(--spacing-xs));
}

.profile-skeleton {
	pointer-events: none;
	user-select: none;
}

.skeleton-block {
	display: block;
	border-radius: var(--border-radius-sm);
	background: var(--neutral-200);
	animation: skeleton-pulse 1.2s ease-in-out infinite;
}

.skeleton-avatar {
	width: var(--space-64);
	height: var(--space-64);
	border-radius: var(--border-radius-full);
	background: color-mix(in srgb, var(--primary-green) 14%, var(--neutral-200));
}

.skeleton-identity,
.skeleton-heading,
.skeleton-field,
.skeleton-copy {
	display: grid;
	gap: var(--spacing-xs);
}

.skeleton-eyebrow {
	width: var(--space-80);
	height: var(--space-10);
}

.skeleton-title {
	width: min(260px, 100%);
	height: var(--space-32);
	border-radius: var(--border-radius-base);
}

.skeleton-line {
	width: min(320px, 100%);
	height: var(--space-14);

	&.medium {
		width: min(240px, 100%);
	}

	&.short {
		width: min(160px, 70%);
	}
}

.skeleton-chip {
	width: 96px;
	height: var(--size-button-s);
	border-radius: var(--border-radius-full);

	&.short {
		width: 72px;
	}
}

.skeleton-section-title {
	width: 180px;
	height: var(--space-24);
	border-radius: var(--border-radius-base);

	&.wide {
		width: min(260px, 60vw);
	}
}

.skeleton-status {
	width: 112px;
	height: var(--size-button-s);
	border-radius: var(--border-radius-full);
}

.skeleton-panel {
	align-items: center;
}

.skeleton-field {
	width: min(420px, 100%);
}

.skeleton-label {
	width: var(--space-80);
	height: var(--space-10);
}

.skeleton-input {
	width: 100%;
	height: var(--space-40);
	border-radius: var(--border-radius-sm);
}

.skeleton-button {
	width: 108px;
	height: var(--space-40);
	border-radius: var(--border-radius-sm);
}

.skeleton-icon {
	width: var(--space-40);
	height: var(--space-40);
	border-radius: var(--border-radius-md);
}

.skeleton-copy {
	flex: 1;
	min-width: 0;
	gap: var(--spacing-sm);
}

.skeleton-card-title {
	width: min(160px, 70%);
	height: var(--space-20);
	border-radius: var(--border-radius-base);
}

.skeleton-language-options {
	border-color: var(--neutral-200);
	background: var(--neutral-100);
}

.skeleton-language-option {
	width: var(--space-64);
	height: var(--space-32);
}

.skeleton-toggle {
	width: 76px;
	height: var(--space-24);
	border-radius: var(--border-radius-full);
}

.skeleton-frequency {
	width: 120px;
	height: var(--space-40);
}

.profile-shell {
	width: min(1120px, calc(100% - var(--spacing-xl) * 2));
	margin: 0 auto;
	padding: var(--spacing-xl) 0 var(--spacing-2xl);
}

.profile-header {
	display: grid;
	grid-template-columns: auto minmax(0, 1fr) auto;
	align-items: center;
	gap: var(--spacing-lg);
	padding: var(--spacing-lg) 0 var(--spacing-xl);
	border-bottom: var(--border-width-thin) solid var(--neutral-200);
}

.avatar {
	display: flex;
	align-items: center;
	justify-content: center;
	width: var(--space-64);
	height: var(--space-64);
	border-radius: var(--border-radius-full);
	background: var(--primary-green);
	color: var(--neutral-0);
	font-size: var(--font-size-2xl);
	font-weight: var(--font-weight-semibold);
	line-height: 1;
}

.identity {
	min-width: 0;

	h1 {
		margin: 0;
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		line-height: var(--line-height-tight);
	}

	.email {
		margin: var(--spacing-xs) 0 0;
		color: var(--neutral-600);
		font-size: var(--font-size-base);
		overflow-wrap: anywhere;
	}
}

.eyebrow {
	margin: 0 0 var(--spacing-xs);
	color: var(--primary-green);
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-semibold);
	letter-spacing: 0;
	text-transform: uppercase;
}

.role-list {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: var(--spacing-xs);
	max-width: 420px;
}

.role-chip {
	display: inline-flex;
	align-items: center;
	min-height: var(--size-button-s);
	padding: var(--spacing-xxs) var(--spacing-sm);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-full);
	background: var(--neutral-0);
	color: var(--neutral-600);
	font-size: var(--font-size-small);
	line-height: var(--line-height-normal);
}

.preferences-section {
	padding-top: var(--spacing-xl);
}

.section-heading {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: var(--spacing-lg);
	margin-bottom: var(--spacing-lg);

	h2 {
		margin: 0;
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		line-height: var(--line-height-tight);
	}
}

.status-pill {
	display: inline-flex;
	align-items: center;
	min-height: var(--size-button-s);
	padding: var(--spacing-xxs) var(--spacing-sm);
	border-radius: var(--border-radius-full);
	background: var(--neutral-0);
	color: var(--neutral-600);
	font-size: var(--font-size-small);
	font-weight: var(--font-weight-medium);
	white-space: nowrap;

	&.dirty {
		background: var(--accent-version-amber-bg);
		color: var(--accent-version-amber-text);
	}

	&.error {
		background: color-mix(in srgb, var(--accent-warning) 12%, var(--neutral-0));
		color: var(--accent-warning);
	}

	&.verified {
		background: var(--accent-highlight);
		color: var(--primary-green);
	}

	&.warning {
		background: var(--accent-version-amber-bg);
		color: var(--accent-version-amber-text);
	}
}

.error-message {
	margin: calc(var(--spacing-sm) * -1) 0 var(--spacing-lg);
	color: var(--accent-warning);
	font-size: var(--font-size-small);
	line-height: var(--line-height-normal);
}

.language-card {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--spacing-lg);
	margin-bottom: var(--spacing-xl);
	padding: var(--spacing-lg);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-0);

	h3 {
		margin: 0;
		font-size: var(--font-size-large);
		font-weight: var(--font-weight-semibold);
		line-height: var(--line-height-tight);
	}
}

.language-options {
	display: inline-grid;
	grid-template-columns: repeat(2, minmax(var(--space-64), 1fr));
	gap: var(--spacing-xxs);
	padding: var(--spacing-xxs);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-100);
}

.language-option {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-height: var(--space-40);
	padding: 0 var(--spacing-md);
	border-radius: var(--border-radius-sm);
	color: var(--neutral-600);
	cursor: pointer;
	font-size: var(--font-size-small);
	font-weight: var(--font-weight-semibold);
	white-space: nowrap;

	input {
		position: absolute;
		opacity: 0;
		pointer-events: none;

		&:focus-visible + span {
			outline: 2px solid var(--primary-green);
			outline-offset: 2px;
		}
	}

	&.selected {
		background: var(--neutral-0);
		color: var(--neutral-900);
		box-shadow: var(--shadow-sm);
	}

	&:has(input:disabled) {
		cursor: not-allowed;
		opacity: 0.56;
	}
}

.notification-heading {
	margin-bottom: var(--spacing-md);

	h3 {
		margin: 0;
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		line-height: var(--line-height-tight);
	}
}

.phone-section {
	padding: var(--spacing-xl) 0;
	border-bottom: var(--border-width-thin) solid var(--neutral-200);
}

.phone-panel {
	display: grid;
	grid-template-columns: minmax(220px, 420px) auto;
	align-items: end;
	justify-content: start;
	gap: var(--spacing-md);
	padding: var(--spacing-lg);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-0);
}

.phone-form-panel {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	max-width: 420px;
	padding: var(--spacing-lg);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-0);

	label {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);

		span {
			padding-bottom: var(--spacing-xxs);
			color: var(--neutral-600);
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.04em;
		}

		input {
			height: var(--space-28);
			padding: 0 var(--spacing-sm);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-sm);
			background: var(--neutral-0);
			color: var(--neutral-900);
			font-size: var(--font-size-small);

			&:focus {
				border-color: var(--primary-green);
				outline: 2px solid color-mix(in srgb, var(--primary-green) 20%, transparent);
				outline-offset: 0;
			}
		}
	}

	.saved-number {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);

		span {
			color: var(--neutral-600);
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.04em;
		}

		strong {
			color: var(--neutral-900);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-medium);
		}

		p {
			margin: 0;
			color: var(--neutral-600);
			font-size: var(--font-size-small);
		}
	}

	.feedback {
		margin: var(--spacing-xxs) 0 0;
		font-size: var(--font-size-small);

		&.success {
			color: var(--primary-green);
		}

		&.error {
			color: var(--accent-warning);
		}
	}

	.buttons {
		display: flex;
		justify-content: flex-end;
		gap: var(--spacing-xs);
	}
}

.warning-message {
	color: var(--neutral-600);
	font-size: var(--font-size-small);
	line-height: var(--line-height-normal);

	&.warning {
		color: var(--accent-version-amber-text);
	}
}

.preference-grid {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: var(--spacing-md);
}

.preference-card {
	display: grid;
	grid-template-rows: auto 1fr;
	gap: var(--spacing-lg);
	min-height: 280px;
	padding: var(--spacing-lg);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-0);
}

.card-main {
	display: flex;
	align-items: flex-start;
	gap: var(--spacing-md);
	min-width: 0;
}

.channel-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: var(--space-40);
	height: var(--space-40);
	border-radius: var(--border-radius-md);
	flex-shrink: 0;

	&.in_app {
		background: rgba(29, 191, 115, 0.12);
		color: var(--primary-green);
	}

	&.email {
		background: rgba(25, 118, 210, 0.12);
		color: var(--accent-info);
	}

	&.sms {
		background: rgba(124, 45, 18, 0.16);
		color: var(--accent-version-amber-text);
	}

	&.language {
		background: rgba(25, 118, 210, 0.12);
		color: var(--accent-info);
	}
}

.card-copy {
	min-width: 0;
}

.title-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--spacing-sm);

	h3 {
		margin: 0;
		font-size: var(--font-size-large);
		font-weight: var(--font-weight-semibold);
		line-height: var(--line-height-tight);
	}
}

.channel-toggle {
	display: inline-flex;
	align-items: center;
	min-height: var(--space-24);
	padding: 0 var(--spacing-sm);
	border: 0;
	border-radius: var(--border-radius-full);
	background: var(--neutral-100);
	color: var(--neutral-600);
	cursor: pointer;
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-semibold);
	white-space: nowrap;

	input {
		position: absolute;
		opacity: 0;
		pointer-events: none;

		&:focus-visible + span {
			outline: 2px solid var(--primary-green);
			outline-offset: 2px;
		}
	}

	&.enabled {
		background: var(--accent-highlight);
		color: var(--primary-green);
	}

	&.disabled {
		cursor: not-allowed;
		opacity: 0.56;
	}
}

.card-copy p {
	margin: var(--spacing-sm) 0 0;
	color: var(--neutral-600);
	font-size: var(--font-size-small);
	line-height: var(--line-height-normal);
}

.card-copy .warning-message {
	margin-top: var(--spacing-sm);
	color: var(--accent-version-amber-text);
}

.frequency-row {
	align-self: end;
	display: grid;
	gap: var(--spacing-sm);
}

.frequency-label {
	color: var(--neutral-600);
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-semibold);
	text-transform: uppercase;
	letter-spacing: 0;
}

.immediate-frequency {
	display: inline-flex;
	align-items: center;
	width: fit-content;
	min-height: var(--space-40);
	padding: 0 var(--spacing-md);
	border: var(--border-width-thin) solid var(--primary-green);
	border-radius: var(--border-radius-sm);
	background: var(--accent-highlight);
	color: var(--primary-green);
	font-size: var(--font-size-small);
	font-weight: var(--font-weight-medium);
	line-height: var(--line-height-normal);

	&.disabled {
		border-color: rgba(29, 191, 115, 0.4);
		color: rgba(29, 191, 115, 0.52);
	}
}

.actions {
	display: flex;
	justify-content: flex-end;
	gap: var(--spacing-sm);
	margin-top: var(--spacing-lg);
}

@keyframes skeleton-pulse {
	0%,
	100% {
		opacity: 0.58;
	}

	50% {
		opacity: 1;
	}
}

@media (prefers-reduced-motion: reduce) {
	.skeleton-block {
		animation: none;
	}

	.profile-fade-enter-active,
	.profile-fade-leave-active {
		transition: none;
	}
}

@media (max-width: 980px) {
	.profile-header {
		grid-template-columns: auto minmax(0, 1fr);
	}

	.role-list {
		grid-column: 1 / -1;
		justify-content: flex-start;
	}

	.preference-grid {
		grid-template-columns: 1fr;
	}

	.preference-card {
		min-height: unset;
	}

	.language-card {
		align-items: stretch;
		flex-direction: column;
	}

	.phone-panel {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 640px) {
	.topbar {
		padding: 0 var(--spacing-md);
	}

	.profile-shell {
		width: min(100% - var(--spacing-md) * 2, 1120px);
		padding-top: var(--spacing-lg);
	}

	.profile-header {
		gap: var(--spacing-md);
		padding-top: var(--spacing-md);
	}

	.avatar {
		width: var(--space-50);
		height: var(--space-50);
		font-size: var(--font-size-xl);
	}

	.identity h1 {
		font-size: var(--font-size-2xl);
	}

	.section-heading {
		align-items: flex-start;
		flex-direction: column;
		gap: var(--spacing-sm);
	}

	.preference-card {
		padding: var(--spacing-md);
	}

	.language-card {
		padding: var(--spacing-md);
	}

	.phone-panel {
		padding: var(--spacing-md);
	}

	.phone-form-panel {
		padding: var(--spacing-md);
	}

	.frequency-row {
		align-self: stretch;
	}

	.actions {
		flex-direction: column-reverse;

		button {
			width: 100%;
		}
	}
}
</style>
